import {
  CircleArrowDown,
  CircleArrowUp,
  SlidersHorizontal,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useHistoryFilters } from "../hooks/useHistoryFilters";
import { getActionVariant } from "../utils";

export const RESOURCE_TYPES = ["dilemma", "topic", "user"] as const;

export const ACTION_OPTIONS = [
  { value: 0, key: "create" },
  { value: 1, key: "update" },
  { value: 2, key: "delete" },
  { value: 3, key: "access" },
] as const;

const HistoryFilterComponent = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useHistoryFilters();

  const hasActiveFilters =
    filters.model_type !== null || filters.action !== null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="relative bg-primary/10 text-primary hover:bg-primary/20 lg:bg-secondary lg:hover:bg-secondary/90 lg:text-gray-500">
          <SlidersHorizontal />
          <span className="hidden lg:inline">{t("keys.filters")}</span>
          {hasActiveFilters && (
            <span className="absolute -top-1 -right-1 size-2.5 rounded-full bg-primary" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="-translate-x-3 sm:-translate-x-6">
        <DropdownMenuGroup>
          <DropdownMenuLabel>
            {t("modules.history.filters.by_resource")}
          </DropdownMenuLabel>
          {RESOURCE_TYPES.map((type) => (
            <DropdownMenuCheckboxItem
              key={type}
              checked={filters.model_type === type}
              onCheckedChange={() =>
                setFilters({
                  model_type: filters.model_type === type ? null : type,
                })
              }
            >
              <Badge variant="secondary">{t(`keys.resources.${type}`)}</Badge>
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuLabel>
            {t("modules.history.filters.by_action")}
          </DropdownMenuLabel>
          {ACTION_OPTIONS.map((option) => (
            <DropdownMenuCheckboxItem
              key={option.value}
              checked={filters.action === option.value}
              onCheckedChange={() =>
                setFilters({
                  action: filters.action === option.value ? null : option.value,
                })
              }
            >
              <Badge variant={getActionVariant(option.key)}>
                {t(`keys.action_types.${option.key}`)}
              </Badge>
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuLabel>
            {t("modules.history.filters.by_date")}
          </DropdownMenuLabel>
          <DropdownMenuCheckboxItem
            checked={filters.ordering === "timestamp"}
            onCheckedChange={() =>
              setFilters({
                ordering:
                  filters.ordering === "timestamp" ? "-timestamp" : "timestamp",
              })
            }
          >
            <CircleArrowUp className="text-gray-500" />
            Asc
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={filters.ordering === "-timestamp"}
            onCheckedChange={() =>
              setFilters({
                ordering:
                  filters.ordering === "-timestamp"
                    ? "timestamp"
                    : "-timestamp",
              })
            }
          >
            <CircleArrowDown className="text-gray-500" />
            Desc
          </DropdownMenuCheckboxItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default HistoryFilterComponent;
